import React from 'react';
import styled from 'styled-components';
import { DataElementContext } from '@/page-components/common/DataElementContext';
import { useAppDispatch, useAppSelector } from '@/store';
import { AUTH_GLOBAL_ERROR_CODES } from '@/constants/authErrorCode';
import { setGlobalAuthenticationError } from '@/modules/casino/store/actions/authentication';
import { useSafeNavigate } from '@/components/utils/hooks/useSafeNavigate';

//import './index.scss';

type AuthErrorPopupProps = {
  children: any;
  styleText: string;
  className: string;
  properties?: any;
};

const ModuleElementDiv = styled.div<{ $styleText: string }>((props) => props.$styleText);

const AuthErrorPopup = (componentProps: AuthErrorPopupProps) => {
  const globalAuthenticationError = useAppSelector((state) => state.authentication.globalAuthenticationError);
  const dispatch = useAppDispatch();
  const navigate = useSafeNavigate();
  const [opened, setOpened] = React.useState(false);

  React.useEffect(() => {

    if (globalAuthenticationError) {
      switch (globalAuthenticationError) {
        case AUTH_GLOBAL_ERROR_CODES.INVALID_TOKEN:
        case AUTH_GLOBAL_ERROR_CODES.ALREADY_LOGGED_IN:
          // These errors should trigger the popup
          setOpened(true);
          break;
        case AUTH_GLOBAL_ERROR_CODES.MIGRATION_IN_PROGRESS:
          navigate('/player-migration');
          // dispatch(setGlobalAuthenticationError(AUTH_GLOBAL_ERROR_CODES.NONE));
          break;
        default:
          // For any other error, we do not open the popup
          setOpened(false);
          return;
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [globalAuthenticationError]);

  const closeHandler = React.useCallback(() => {
    setOpened(false);
    // Reset the global authentication error
    dispatch(setGlobalAuthenticationError(AUTH_GLOBAL_ERROR_CODES.NONE));
  }, [dispatch]);

  const toLogin = React.useCallback(() => {
    closeHandler();
    navigate('/login');
  }, [closeHandler, navigate]);

  const toHome = React.useCallback(() => {
    closeHandler();
    navigate('/');
  }, [closeHandler, navigate]);

  const contextValue = React.useMemo(
    () => ({
      opened,
      closeHandler,
      modalHeaderText: `AUTH_ERROR_POPUP_HEADER_${globalAuthenticationError}`, // _INVALID_TOKEN => token expired or invalid; ALREADY_LOGGED_IN => user already logged in and trying to log in again with an invalid or expired token
      modalContentTitle: `AUTH_ERROR_POPUP_TITLE_${globalAuthenticationError}`,
      modalContentDescription: `AUTH_ERROR_POPUP_DESCRIPTION_${globalAuthenticationError}`,
      modalContentExtraInfo: `AUTH_ERROR_POPUP_EXTRA_INFO_${globalAuthenticationError}`,
      modalFooterPrimaryCTAText:
        globalAuthenticationError === AUTH_GLOBAL_ERROR_CODES.ALREADY_LOGGED_IN
          ? `AUTH_ERROR_POPUP_TO_HOME_${globalAuthenticationError}`
          : `AUTH_ERROR_POPUP_TO_LOGIN_${globalAuthenticationError}`,
      modalFooterPrimaryCTAHandler:
        globalAuthenticationError === AUTH_GLOBAL_ERROR_CODES.ALREADY_LOGGED_IN ? toHome : toLogin,
      modalFooterSecondaryCTAText: `AUTH_ERROR_POPUP_CLOSE_${globalAuthenticationError}`,
      modalFooterSecondaryCTAHandler: closeHandler,
      showPrimaryCTA: window.config.appEnv !== 'poker',
      showSecondaryCTA: window.config.appEnv !== 'poker',
    }),
    [opened, closeHandler, globalAuthenticationError, toHome, toLogin],
  );

  return (
    <ModuleElementDiv className={componentProps.className ?? ''} $styleText={componentProps.styleText}>
      <DataElementContext.Provider value={contextValue}>{componentProps.children}</DataElementContext.Provider>
    </ModuleElementDiv>
  );
};

export default AuthErrorPopup;
